import moment from "moment";

export const EMPTY_VALUE = "—";

// What to call the actor of a row that has no named person (orderStatusHistory.source).
const SOURCE_LABELS = {
    USER_API: "Customer app",
    ADMIN_API: "Team",
    WEBHOOK: "Webhook",
    CRON: "Scheduled job",
    SYSTEM: "System",
};

const sentenceCase = (text) => {
    const lowered = text.toLowerCase();
    return lowered.charAt(0).toUpperCase() + lowered.slice(1);
};

/** "SHIPPED_IN_TRANSIT" -> "Shipped in transit". */
export const humanizeStatus = (status) => (status ? sentenceCase(String(status).replace(/_/g, " ")) : EMPTY_VALUE);

/** "addressId" -> "Address", "noOfDays" -> "No of days". */
export const humanizeField = (field) => {
    const withoutIdSuffix = field.replace(/Id$/, "") || field;
    return sentenceCase(withoutIdSuffix.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/_/g, " "));
};

export const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return EMPTY_VALUE;
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

const sourceLabel = (source) => SOURCE_LABELS[source] || humanizeStatus(source);

// A quotation's field is told apart from the order's: "Address" vs "Quotation address".
const toChange = (change, { isQuotation = false } = {}) => {
    const isStatus = change.field === "status";
    const label = humanizeField(change.field);
    return {
        label: isQuotation ? `Quotation ${label.toLowerCase()}` : label,
        from: isStatus ? humanizeStatus(change.from) : formatValue(change.from),
        to: isStatus ? humanizeStatus(change.to) : formatValue(change.to),
    };
};

const toEditEntry = (edit) => ({
    key: `edit-${edit.id}`,
    occurredAt: edit.occurredAt,
    actorLabel: edit.actor?.name || "System",
    isByPerson: Boolean(edit.actor),
    changes: (edit.changes || []).map((change) => toChange(change, { isQuotation: edit.event === "QUOTATION_EDITED" })),
});

const toTransitionEntry = (transition, index) => ({
    key: `status-${index}`,
    occurredAt: transition.createdAt,
    actorLabel: transition.actor?.name || sourceLabel(transition.source),
    isByPerson: Boolean(transition.actor),
    changes: [toChange({ field: "status", from: transition.fromStatus, to: transition.toStatus })],
});

/**
 * A status edit made through the ops API is recorded twice by the backend: as an ops edit
 * (journeyEvents) and as a status transition (orderStatusHistory), both stamped with the same
 * requestId. The edit already shows the status change, so drop the transition it mirrors.
 * Only order edits mirror an order's transitions; a quotation's own status is a different
 * thing. Rows without a requestId are never merged.
 */
const dropMirroredTransitions = (transitions, edits) => {
    const mirrored = new Set();
    edits.forEach((edit) => {
        if (!edit.requestId || edit.entity?.type !== "USER_ORDER") return;
        const statusChange = (edit.changes || []).find((change) => change.field === "status");
        if (statusChange) mirrored.add(`${edit.requestId}|${statusChange.to}`);
    });
    return transitions.filter((transition) => !(transition.requestId && mirrored.has(`${transition.requestId}|${transition.toStatus}`)));
};

/**
 * Merge the two lists GET /ops/orders/:orderId/history returns -- status `transitions` and
 * ops `edits` -- into one list of display entries, newest first. The API sends each list
 * oldest first; ops reviewing an order care about the latest change, so we reverse here.
 */
export function buildTimeline({ transitions = [], edits = [] } = {}) {
    const entries = [
        ...dropMirroredTransitions(transitions, edits).map(toTransitionEntry),
        ...edits.map(toEditEntry),
    ];
    return entries.sort((a, b) => moment(b.occurredAt).valueOf() - moment(a.occurredAt).valueOf());
}
